import { Injectable } from '@angular/core';
import {
  catchError,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, Observable, of, OperatorFunction, Subject } from 'rxjs';
import {
  EServiceSocketMessageTypeDTO,
  ESocketMessageTypeDTO,
  IChatMessage,
  IUserConnectedEventDTO,
  IUserDisconnectedEventDTO,
  IServiceMessageEvent,
  IServiceMessageEventDTO,
  IQueueInfoUpdateEvent,
  IQueueInfoUpdateEventDTO,
  TChatSocketMessageType,
  IChatUpdateEvent,
  IChatUpdateEventDTO,
  IUserConnectedEvent,
  IUserDisconnectedEvent,
  UserConnectionEventSocketMessageViewModelFactory,
  ChatMessageEventSocketMessageViewModelFactory,
  QueueInfoUpdateEventSocketMessageViewModelFactory,
  ServiceMessageEventSocketMessageViewModelFactory,
  IChatMessageEventDTO,
  ISocketMessage,
  ChatUpdateEventSocketMessageViewModelFactory,
  IRequisitionUpdateEvent,
  IRequisitionUpdateEventDTO,
  RequisitionUpdateEventSocketMessageViewModelFactory,
  IDoctorStatusUpdateEvent,
  IDoctorStatusUpdateEventDTO,
  DoctorStatusUpdateEventSocketMessageViewModelFactory,
} from '@project/view-models';
import { IFeatureService } from '@project/services';
import { environment } from '@env';
import { ConstWsSocketMessagesEnum } from '../shared/ws-socket.messages.enum';
import { WebsocketLifecycleService } from '@core/realtime/websocket';
import { FirestoreWebsocketTriggerService } from '../services/firestore-websocket-trigger.service';

/**
 * TODO:
 *  * Thinks about extract messages streams to different classes (by type maybe);
 */
@Injectable({
  providedIn: 'root',
})
export class SocketMessagesDataProviderService implements IFeatureService {
  private _isInitialized$ = new BehaviorSubject<boolean>(false);

  private socketMessage$: Observable<ISocketMessage> = this.websocketLifecycleService.messages$.pipe(
    filter(() => this._isInitialized$.value),
    map((messageString: string) => {
      try {
        const parsed = JSON.parse(messageString);
        if (parsed && parsed.event && parsed.data) {
          return parsed.data;
        }
        return parsed;
      } catch (e) {
        return null;
      }
    }),
    filter((message) => !!message),
    map((message) => {
      try {
        if (!message.attachment) return message;

        if (typeof message.attachment === 'string') {
          message.attachment = JSON.parse(message.attachment);
        }

        message.text = message.attachment.original;

        return message;
      } catch (e) {
        return message;
      }
    }),
    tap((message) => {
      if (!environment.production) {
        const now = new Date();
        console.log(`---> Socket Message :: ${now.toLocaleTimeString()}`, message);
      }
    }),
    share(),
  );

  public disconnectConflict$: Observable<void> = this.socketMessage$.pipe(
    this.filterByTypes([ConstWsSocketMessagesEnum.DisconnectThroughMultipleDevices]),
    map(() => null),
  );

  public chatMessage$: Observable<IChatMessage> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.ChatMessage]),
    map((messageDto: IChatMessageEventDTO) =>
      ChatMessageEventSocketMessageViewModelFactory.createMessageFromDTO(messageDto),
    ),
  );

  public queueInfoUpdate$: Observable<IQueueInfoUpdateEvent> = this.socketMessage$.pipe(
    this.filterByTypes([
      ESocketMessageTypeDTO.ReceptionistQueueInformation,
      ESocketMessageTypeDTO.PatientsQueueInformation,
      ESocketMessageTypeDTO.DoctorsQueueInformation,
    ]),
    map((messageDto: IQueueInfoUpdateEventDTO) =>
      QueueInfoUpdateEventSocketMessageViewModelFactory.createMessageFromDto(messageDto),
    ),
  );

  public callStarted$: Observable<IServiceMessageEvent> = this.socketMessage$.pipe(
    this.filterByTypes([EServiceSocketMessageTypeDTO.CallStarted]),
    map((serviceMessageDto: IServiceMessageEventDTO) =>
      ServiceMessageEventSocketMessageViewModelFactory.createEventMessageFromDTO(serviceMessageDto),
    ),
  );

  public callEnded$: Observable<IServiceMessageEvent> = this.socketMessage$.pipe(
    this.filterByTypes([EServiceSocketMessageTypeDTO.CallEnded]),
    map((serviceMessageDto: IServiceMessageEventDTO) =>
      ServiceMessageEventSocketMessageViewModelFactory.createEventMessageFromDTO(serviceMessageDto),
    ),
  );

  public callDeclined$: Observable<IServiceMessageEvent> = this.socketMessage$.pipe(
    this.filterByTypes([EServiceSocketMessageTypeDTO.CallDeclined]),
    map((serviceMessageDto: IServiceMessageEventDTO) =>
      ServiceMessageEventSocketMessageViewModelFactory.createEventMessageFromDTO(serviceMessageDto),
    ),
  );

  /**
   * @deprecated
   * use requisitionUpdated$
   */
  public chatCreated$: Observable<IChatUpdateEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.ChatCreated]),
    map((serviceMessageDto: IChatUpdateEventDTO) =>
      ChatUpdateEventSocketMessageViewModelFactory.createChatEventFromDTO(serviceMessageDto),
    ),
  );

  /**
   * @deprecated
   * use requisitionUpdated$
   */
  public chatArchived$: Observable<IChatUpdateEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.ChatArchived]),
    map((serviceMessageDto: IChatUpdateEventDTO) =>
      ChatUpdateEventSocketMessageViewModelFactory.createChatEventFromDTO(serviceMessageDto),
    ),
  );

  public awake$: Observable<IServiceMessageEvent> = this.socketMessage$.pipe(
    this.filterByTypes([EServiceSocketMessageTypeDTO.Awake]),
    map((serviceMessageDto: IServiceMessageEventDTO) =>
      ServiceMessageEventSocketMessageViewModelFactory.createEventMessageFromDTO(serviceMessageDto),
    ),
  );

  public userConnected$: Observable<IUserConnectedEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.UserConnected]),
    map((messageDto: IUserConnectedEventDTO) =>
      UserConnectionEventSocketMessageViewModelFactory.createConnectedEventFromDTO(messageDto),
    ),
  );

  public userDisconnected$: Observable<IUserDisconnectedEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.UserDisconnected]),
    map((messageDto: IUserDisconnectedEventDTO) =>
      UserConnectionEventSocketMessageViewModelFactory.createDisconnectedEventFromDTO(messageDto),
    ),
  );

  public requisitionUpdated$: Observable<IRequisitionUpdateEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.RequisitionInformation]),
    map((dto: IRequisitionUpdateEventDTO) => RequisitionUpdateEventSocketMessageViewModelFactory.createFromDTO(dto)),
  );

  public doctorStatusUpdated$: Observable<IDoctorStatusUpdateEvent> = this.socketMessage$.pipe(
    this.filterByTypes([ESocketMessageTypeDTO.DoctorStatusUpdate]),
    map((dto: IDoctorStatusUpdateEventDTO) => DoctorStatusUpdateEventSocketMessageViewModelFactory.createFromDTO(dto)),
  );

  public isOnline$ = this.websocketLifecycleService.isOnline$.pipe(filter(() => this._isInitialized$.value));
  public isOffline$ = this.websocketLifecycleService.isDown$.pipe(filter(() => this._isInitialized$.value));

  private destroy$ = new Subject();

  private _reconnected$ = new Subject<void>();
  public readonly reconnected$ = this._reconnected$.asObservable();

  constructor(
    private websocketLifecycleService: WebsocketLifecycleService,
    private firestoreWebsocketTriggerService: FirestoreWebsocketTriggerService,
  ) {}

  public initialise(): Observable<void> {
    if (this._isInitialized$.value) return of(null);

    this._isInitialized$.next(true);

    this.listenToSocketDefinitionToInitialize();

    return this.firestoreWebsocketTriggerService.resolveInitialProvider().pipe(
      map(() => null),
      takeUntil(this.destroy$),
      defaultIfEmpty(null),
    );
  }

  public destroy(): Observable<void> {
    this.destroy$.next();
    this.websocketLifecycleService.destroy();
    this._isInitialized$.next(false);
    return of(null);
  }

  private listenToSocketDefinitionToInitialize() {
    this.websocketLifecycleService.provider$
      .pipe(
        filter((provider) => !!provider),
        distinctUntilChanged(),
        switchMap(() => this.websocketLifecycleService.connect().pipe(catchError(() => of(null)))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private filterByTypes(types: TChatSocketMessageType[]): OperatorFunction<ISocketMessage, ISocketMessage> {
    const normalizedTypes = types.map((type) => type.toLowerCase());

    return filter((message: ISocketMessage) => normalizedTypes.includes(message.type.toLowerCase()));
  }
}
